import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getOidcConfig } from '../../../auth/config'
import { startLogin, startLogout } from '../../../auth/oidc'
import { claimOidcReauthorization, clearOidcOverride, clearOidcReauthorization, clearOidcRetry, clearSession, loadOidcOverride, loadOidcRetry, loadSession, saveIamNewUserHint, saveSession, setFlashToast, takeFlashToast } from '../../../auth/state'
import { useToast } from '../../../components/Toast'

function normalizeHintToken(s) {
  return String(s || '')
    .trim()
    .slice(0, 200)
    .replace(/[^a-zA-Z0-9:_\\-\\.]/g, '')
}

function parseAutoMode(qs) {
  const raw = String(qs.get('auto') || '').trim().toLowerCase()
  if (raw === '0' || raw === 'false' || raw === 'off') return false
  return true
}

function localDevAccessToken() {
  const payload = btoa(JSON.stringify({
    sub: 'dev-admin',
    typ: 'access',
    aud: 'chamii-main',
    client_id: 'chamii-main',
    azp: 'chamii-main',
    exp: Math.floor(Date.now() / 1000) + 8 * 60 * 60,
    roles: ['ADMIN'],
  })).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return `local.${payload}.dev`
}

function useLoginParams() {
  const location = useLocation()
  const qs = new URLSearchParams(location.search)
  const n = qs.get('next') || '/monitor'
  const next = n.startsWith('/') && !n.startsWith('//') && !n.includes('\\') ? n : '/monitor'
  const auto = parseAutoMode(qs)
  const iamNewUser = qs.get('iam_new_user') === '1' || qs.get('iam_new_user') === 'true'
  const iamSub = normalizeHintToken(qs.get('iam_sub') || '')
  const iamApp = normalizeHintToken(qs.get('iam_app') || '')
  // Force fresh login - clear any stale session
  const fresh = qs.get('fresh') === '1' || qs.get('fresh') === 'true'
  const reauth = qs.get('reauth') === '1' || qs.get('reauth') === 'true'
  return { next, auto, iamNewUser, iamSub, iamApp, fresh, reauth }
}

export default function Login() {
  const navigate = useNavigate()
  const { next, auto, iamNewUser, iamSub, iamApp, fresh, reauth } = useLoginParams()
  const cfg = useMemo(() => getOidcConfig(), [])
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const { toast } = useToast()
  const oidcOverride = loadOidcOverride()
  const oidcRetry = loadOidcRetry()

  const [existing, setExisting] = useState(() => loadSession())

  // Clear stale session if fresh=1
  useEffect(() => {
    if (!fresh) return
    clearSession()
    setExisting(null)
  }, [fresh])

  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [mode, setMode] = useState('idle') // idle | redirecting
  const startedRef = useRef(false)

  useEffect(() => {
    const t = takeFlashToast()
    if (t) toast(t)
  }, [toast])

  useEffect(() => {
    if (!iamNewUser || !iamSub) return
    saveIamNewUserHint({ sub: iamSub, app: iamApp || undefined, at: Date.now() })
  }, [iamApp, iamNewUser, iamSub])

  // Auto-redirect to IAM login if auto mode is enabled and no valid session
  useEffect(() => {
    // Skip if already started, has existing session, no config, or auto disabled
    if (startedRef.current) return
    if (existing) return
    if (!cfg) return
    if (!auto) return
    if (reauth && !claimOidcReauthorization()) return

    startedRef.current = true
    setErr('')
    setBusy(true)
    setMode('redirecting')

    // Directly start login - no health check needed for trusted domains
    startLogin(next).catch((e) => {
      setErr(String(e?.message || e))
      setBusy(false)
      setMode('idle')
    })
  }, [auto, cfg, existing, next, reauth])

  if (existing) {
    return (
      <div className="chamii-viewport chamii-safe-top chamii-safe-bottom bg-dots chamii-auth-shell flex items-center justify-center p-6">
        <div className="w-full max-w-lg bg-brand-surface rounded-[3rem] p-3 shadow-2xl border-2 border-brand-textDark/10">
          <div className="bg-brand-yellow rounded-[2.5rem] px-10 py-12 text-center">
            <div className="w-24 h-24 bg-white rounded-full mx-auto flex items-center justify-center shadow-lg shadow-brand-yellow/20 border-4 border-brand-yellowDark">
              <span className="text-3xl font-black text-brand-textDark">✓</span>
            </div>
            <div className="mt-7 text-4xl font-black tracking-tight text-brand-textDark">Signed in</div>
            <div className="mt-3 text-brand-text font-bold">Continue to the console.</div>
            <div className="mt-8 grid gap-3">
              <button
                type="button"
                className="btn-pushable w-full rounded-2xl bg-brand-pink text-white font-extrabold py-4 shadow-[0_10px_0_#E04848]"
                onClick={() => navigate(next, { replace: true })}
              >
                Continue
              </button>
              <button
                type="button"
                className="w-full rounded-2xl bg-white/70 text-brand-textDark font-extrabold py-4 border-2 border-white hover:bg-white transition-colors"
                onClick={() => {
                  startedRef.current = true
                  setFlashToast({ kind: 'success', title: 'Signed out', message: 'Logout successful.' })
                  startLogout()
                  setExisting(null)
                }}
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const headline = mode === 'redirecting' ? 'Redirecting…' : 'Welcome Back'
  const subline = mode === 'redirecting' ? 'Redirecting to Chamii IAM…' : 'Sign in via Chamii IAM to continue.'

  return (
    <div className="chamii-viewport chamii-safe-top chamii-safe-bottom bg-dots chamii-auth-shell flex items-center justify-center p-6">
      <div className="w-full max-w-lg bg-brand-surface rounded-[3rem] p-3 shadow-2xl border-2 border-brand-textDark/10">
        <div className="bg-brand-yellow rounded-[2.5rem] px-10 py-12 text-center">
          <div className="w-24 h-24 bg-white rounded-full mx-auto flex items-center justify-center shadow-lg shadow-brand-yellow/20 border-4 border-brand-yellowDark">
            {busy ? (
              <div className="w-10 h-10 border-[5px] border-brand-textDark border-t-transparent rounded-full animate-spin" />
            ) : (
              <img src="/chamii-logo-icon-only.png" alt="Chamii" className="w-12 h-12 object-contain" />
            )}
          </div>

          <div className="mt-7 text-4xl font-black tracking-tight text-brand-textDark">{headline}</div>
          <div className="mt-3 text-brand-text font-bold">{subline}</div>

          {!cfg && (
            <div className="mt-6 rounded-2xl border-2 border-brand-pink/20 bg-brand-pink/10 px-4 py-3 text-brand-pinkDark font-extrabold text-sm">
              Missing OIDC config on this build.
            </div>
          )}
          {err && (
            <div className="mt-6 rounded-2xl border-2 border-brand-pink/20 bg-brand-pink/10 px-4 py-3 text-brand-pinkDark font-extrabold text-sm break-words">
              {err}
            </div>
          )}

          <div className="mt-8 grid gap-3">
            <button
              type="button"
              disabled={!cfg || busy}
              className={`btn-pushable w-full rounded-2xl bg-brand-pink text-white font-extrabold py-4 shadow-[0_10px_0_#E04848] ${
                !cfg || busy ? 'opacity-60 cursor-not-allowed shadow-none' : ''
              }`}
              onClick={() => {
                if (!cfg || busy) return
                clearOidcReauthorization()
                setErr('')
                setBusy(true)
                setMode('redirecting')
                startLogin(next).catch((e) => {
                  setErr(String(e?.message || e))
                  setBusy(false)
                  setMode('idle')
                })
              }}
            >
              Continue with Chamii IAM
            </button>

            {isLocalhost && (
              <button
                type="button"
                className="w-full rounded-2xl bg-white/70 text-brand-textDark font-extrabold py-4 border-2 border-white hover:bg-white transition-colors"
                onClick={() => {
                  saveSession({ tokens: { accessToken: localDevAccessToken() }, obtainedAt: Date.now(), user: { preferred_username: 'dev-admin', roles: ['ADMIN'] } })
                  navigate(next, { replace: true })
                }}
              >
                Dev mode enter
              </button>
            )}

            {err && (
              <button
                type="button"
                className="w-full rounded-2xl bg-white/70 text-brand-textDark font-extrabold py-4 border-2 border-white hover:bg-white transition-colors"
                onClick={() => {
                  clearSession()
                  window.location.replace('/login?fresh=1')
                }}
              >
                Clear session & retry
              </button>
            )}

            {err && (oidcOverride || (oidcRetry && oidcRetry.count > 0)) && (
              <button
                type="button"
                className="w-full rounded-2xl bg-white/70 text-brand-textDark font-extrabold py-4 border-2 border-white hover:bg-white transition-colors"
                onClick={() => {
                  clearSession()
                  clearOidcOverride()
                  clearOidcRetry()
                  window.location.replace('/login?fresh=1&auto=0')
                }}
              >
                Reset login config
              </button>
            )}
          </div>

          <div className="mt-8 text-[11px] font-black text-brand-text tracking-widest uppercase">OIDC Login</div>
        </div>
      </div>
    </div>
  )
}
